/**
 * VideoCallDetailModal.tsx
 *
 * Large modal for a single video call. Left column: the recording with a
 * Video ⇄ Transcript segmented toggle beneath it (transcript is timecoded +
 * speaker-labeled for newly transcribed calls, plain text otherwise, with a
 * search box). Right column: the AI scorecard (auto-generated on first open and
 * cached). Opened from the Video Calls list (VideoCallsPage).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Search, Video as VideoIcon } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/utils';
import {
  getVideoCallScorecard,
  generateVideoCallScorecard,
  type TranscriptSegment,
} from '@/services/videoCalls.service';
import type { VideoCallListItem } from '@/services/videoCalls.service';
import { VideoCallScorecardPanel, type ScorecardEmptyReason } from '@/components/VideoCallScorecardPanel';

function formatDateTime(value: string | null | undefined): string {
  if (!value || value.startsWith('0000')) return '—';
  const date = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function mmss(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

type LeftTab = 'video' | 'transcript';

// ─── Transcript view (segments or plain text) with client-side search ────────

function TranscriptView({
  segments,
  text,
}: {
  segments: TranscriptSegment[];
  text: string;
}) {
  const [q, setQ] = useState('');
  const query = q.trim().toLowerCase();

  const plainLines = useMemo(
    () => text.split('\n').map((l) => l.trim()).filter(Boolean),
    [text],
  );

  const hasSegments = segments.length > 0;
  const filteredSegments = hasSegments
    ? segments.filter((s) => !query || s.text.toLowerCase().includes(query))
    : [];
  const filteredLines = !hasSegments
    ? plainLines.filter((l) => !query || l.toLowerCase().includes(query))
    : [];

  const empty = !hasSegments && plainLines.length === 0;

  return (
    <div className="flex h-full flex-col bg-white">
      {!empty && (
        <div className="shrink-0 border-b border-slate-200 p-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search through the conversation"
              className="pl-8"
            />
          </div>
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        {empty ? (
          <p className="mt-8 text-center text-sm italic text-slate-400">
            No transcript available.
          </p>
        ) : hasSegments ? (
          <div className="space-y-3">
            {filteredSegments.map((s, i) => (
              <div key={i} className="flex gap-3">
                <span className="w-10 shrink-0 pt-0.5 font-mono text-xs font-medium text-indigo-500">
                  {mmss(s.start)}
                </span>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-slate-500">{s.speaker}</p>
                  <p className="text-sm leading-relaxed text-slate-700">{s.text}</p>
                </div>
              </div>
            ))}
            {filteredSegments.length === 0 && (
              <p className="text-center text-sm italic text-slate-400">No matches.</p>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {filteredLines.map((l, i) => (
              <p key={i} className="text-sm leading-relaxed text-slate-700">{l}</p>
            ))}
            {filteredLines.length === 0 && (
              <p className="text-center text-sm italic text-slate-400">No matches.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Modal ───────────────────────────────────────────────────────────────────

export function VideoCallDetailModal({
  videoCall,
  onClose,
}: {
  videoCall: VideoCallListItem;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<LeftTab>('video');
  const [videoError, setVideoError] = useState(false);
  const autoGenRef = useRef(false);

  // Scoring runs async on the worker (GenerateVideoCallScorecardJob) — the
  // POST just kicks it off and resolves almost immediately with
  // {status: 'pending'}. We detect completion by polling until the cached
  // scorecard's updatedDate moves past what it was right before we asked for
  // a (re)generation. `updatedDate` (not the ID) is the right marker since
  // Regenerate updates the existing row in place rather than inserting a new one.
  const POLL_INTERVAL_MS = 3_000;
  const POLL_TIMEOUT_MS = 90_000;
  const [polling, setPolling] = useState(false);
  const [pollTimedOut, setPollTimedOut] = useState(false);
  const pollBaselineRef = useRef<string | null>(null);
  const pollStartedAtRef = useRef(0);

  useEffect(() => {
    setVideoError(false);
    setTab('video');
    autoGenRef.current = false;
    setPolling(false);
    setPollTimedOut(false);
  }, [videoCall.videoCallID]);

  const patientName = videoCall.patientName?.trim() || 'Unknown patient';
  const hasRecording =
    videoCall.compositionStatus === 'available' && !!videoCall.recordingUrl && !videoError;

  const queryKey = ['video-call-scorecard', videoCall.videoCallID] as const;
  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: ({ signal }) => getVideoCallScorecard(videoCall.videoCallID, { signal }),
    staleTime: 60_000,
    refetchInterval: polling ? POLL_INTERVAL_MS : false,
  });

  // Stop polling once a freshly (re)scored card lands, or after the timeout.
  useEffect(() => {
    if (!polling) return;
    const updatedDate = data?.scorecard?.updatedDate ?? null;
    if (updatedDate !== null && updatedDate !== pollBaselineRef.current) {
      setPolling(false);
      return;
    }
    if (Date.now() - pollStartedAtRef.current > POLL_TIMEOUT_MS) {
      setPolling(false);
      setPollTimedOut(true);
    }
  }, [polling, data]);

  const genMutation = useMutation({
    mutationFn: (vars: { regenerate?: boolean }) =>
      generateVideoCallScorecard(videoCall.videoCallID, vars),
    onMutate: () => {
      pollBaselineRef.current = data?.scorecard?.updatedDate ?? null;
      pollStartedAtRef.current = Date.now();
      setPollTimedOut(false);
      setPolling(true);
    },
    onError: () => setPolling(false),
  });

  const transcript = data?.transcript ?? { text: '', segments: [] };
  const hasTranscript = !!(transcript.text.trim() || transcript.segments.length);

  // Auto-generate once on first open when a template + transcript exist and no
  // scorecard is cached yet.
  useEffect(() => {
    if (!data || autoGenRef.current) return;
    if (data.scorecard || !data.template || !hasTranscript) return;
    autoGenRef.current = true;
    genMutation.mutate({ regenerate: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, hasTranscript]);

  const emptyReason: ScorecardEmptyReason = !data
    ? null
    : !data.template
      ? 'no-template'
      : !hasTranscript
        ? 'no-transcript'
        : null;

  const title = (
    <div className="min-w-0">
      <h2 className="truncate text-base font-semibold text-slate-900">Video Call — {patientName}</h2>
      <p className="mt-0.5 text-xs text-slate-500">
        {formatDateTime(videoCall.createDate)}
        {videoCall.durationFormatted ? ` · ${videoCall.durationFormatted}` : ''}
      </p>
    </div>
  );

  return (
    <Modal isOpen onClose={onClose} title={title} size="xxl" bodyClassName="p-0">
      <div className="flex h-[80vh] flex-col md:flex-row">
        {/* Left: recording / transcript with a toggle beneath */}
        <div className="flex min-h-0 flex-1 flex-col border-b border-slate-200 md:border-b-0">
          <div className="min-h-0 flex-1 overflow-hidden">
            {tab === 'video' ? (
              <div className="flex h-full w-full items-center justify-center bg-black">
                {hasRecording ? (
                  <video
                    controls
                    preload="metadata"
                    src={videoCall.recordingUrl ?? undefined}
                    onError={() => setVideoError(true)}
                    className="h-full w-full object-contain"
                  >
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <div className="flex flex-col items-center gap-3 px-6 text-center text-slate-300">
                    <VideoIcon className="h-12 w-12 text-slate-500" />
                    <p className="text-sm">
                      {videoError
                        ? 'Recording could not be loaded.'
                        : (videoCall.recordingMessage ?? 'Recording not available')}
                    </p>
                    {videoError && videoCall.recordingUrl && (
                      <a
                        href={videoCall.recordingUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm text-blue-400 underline underline-offset-2 hover:text-blue-300"
                      >
                        Open directly
                      </a>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <TranscriptView segments={transcript.segments} text={transcript.text} />
            )}
          </div>

          {/* Video ⇄ Transcript toggle */}
          <div className="flex shrink-0 justify-center border-t border-slate-200 bg-white p-2">
            <div className="inline-flex rounded-lg bg-slate-100 p-1">
              {(['video', 'transcript'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTab(t)}
                  className={cn(
                    'rounded-md px-4 py-1.5 text-sm font-medium capitalize transition-colors',
                    tab === t ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700',
                  )}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right: AI scorecard */}
        <div className="min-h-0 w-full shrink-0 border-l border-slate-200 md:w-[420px]">
          <VideoCallScorecardPanel
            scorecard={data?.scorecard ?? null}
            templateName={data?.template?.name}
            ratingMin={data?.template?.ratingMin ?? 1}
            ratingMax={data?.template?.ratingMax ?? 5}
            generating={isLoading || genMutation.isPending || polling}
            error={
              genMutation.isError
                ? 'Failed to generate scorecard.'
                : pollTimedOut
                  ? 'This is taking longer than usual — check back shortly.'
                  : null
            }
            emptyReason={emptyReason}
            onRegenerate={() => genMutation.mutate({ regenerate: true })}
          />
        </div>
      </div>
    </Modal>
  );
}
